import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import handshakeIcon from "src/assets/svg-icons/aggregators/handshake.svg";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  useAddAggCostAndFeesMutation,
  useGetAggJourneys,
  useGetAggregatorCostAndFeeById,
  useGetAggregatorsList,
  useUpdateAggCostAndFeesMutation,
} from "src/api/aggreagators";
import { useGetBranches } from "src/api/generic";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import {
  downloadFileFromUrl,
  formatBytes,
  specificBranchHasPermission,
  toNum,
} from "src/utils";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import Keta from "src/assets/img/pos/keta.jpg";
import HungerStation from "src/assets/img/pos/hunger_station.jpg";
import Jahez from "src/assets/img/pos/jahez.jpg";
import Mrsool from "src/assets/img/pos/mrsool.jpg";
import Noon from "src/assets/img/pos/noon.jpg";
import FileItem from "src/shared/components/file-item";

const schema = yup.object().shape({
  branch_id: yup.number().required("Required"),
  journeys: yup
    .array()
    .of(yup.number().required("Required"))
    .required("Required")
    .min(1, "Required"),
  aggregator_id: yup.string().required("Required"),
  agg_fee: yup.number().typeError("Required").required("Required"),
  delivery_cost: yup.number().typeError("Required").required("Required"),
  bank_fee: yup.number().typeError("Required").required("Required"),
  other_fee: yup.number().typeError("Required").required("Required"),
  other_cost: yup.number().typeError("Required").required("Required"),
  compensation: yup.number().typeError("Required").required("Required"),
  compensationTax: yup.number().typeError("Required").required("Required"),
  payment: yup.string().required("Required"),
  restaurantDiscount: yup.number().typeError("Invalid"),
  otherRevenue: yup.number().typeError("Required").required("Required"),
  confirmedCompensation: yup
    .number()
    .typeError("Required")
    .required("Required"),
  prevMonthSales: yup.number().typeError("Required").required("Required"),
  nextMonthSales: yup.number().typeError("Required").required("Required"),
  note: yup.string(),
  sendEmail: yup.boolean(),
  salesAsAggInv: yup.number().typeError("Required").required("Required"),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const AddEditAggregatorFees = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation("aggregators");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [mode, setMode] = useState<"add" | "edit">("add");
  const [selectedJourneys, setSelectedJourneys] = useState<any[]>([]);
  const [files, setFiles] = useState<any>([]);

  const [aggCalcs, setAggCalcs] = useState({
    basicAggFees: 0,
    deliverFees: 0,
    feeTax: 0,
    deliveryFeeTax: 0,
    salesWithTax: 0,
  });

  useEffect(() => {
    if (params.id) {
      setMode("edit");
    }
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      journeys: [],
      prevMonthSales: 0,
      nextMonthSales: 0,
    },
  });

  // APIS
  const { data: branchesData } = useGetBranches();
  const { mutate, status, error, isPending } = useAddAggCostAndFeesMutation();
  const {
    mutate: mutateUpdate,
    error: errorUpdate,
    status: statusUpdate,
    isPending: isPendingUpdate,
  } = useUpdateAggCostAndFeesMutation();
  const {
    data,
    status: getAggFeeByIdStatus,
    refetch,
  } = useGetAggregatorCostAndFeeById({
    enabled: !!params.id,
    id: params.id as string,
  });
  const { data: journeysData } = useGetAggJourneys({
    branch_id: watch("branch_id"),
    aggregator_id: watch("aggregator_id"),
  });

  const { data: aggregatorsData } = useGetAggregatorsList({
    enabled: !!watch("branch_id"),
    branches: [`${watch("branch_id")}`],
  });

  const aggregatorId = watch("aggregator_id");

  const selectedAgg = useMemo(() => {
    if (!aggregatorsData?.data || !aggregatorId) return undefined;
    return aggregatorsData.data.find(
      (agg: any) => agg.id === Number(aggregatorId)
    );
  }, [aggregatorsData, aggregatorId]);

  const selectedBranch = branchesData?.data.find((branch) => {
    return branch.id === +watch("branch_id");
  });

  // permissions
  const branchesHavePermission =
    branchesData?.data.filter(
      (branch) =>
        branch.status &&
        specificBranchHasPermission("add_aggregator_cost_and_fee", branch.id)
    ) || [];

  let totalCommissionExpected = 0;

  selectedJourneys.forEach((journey) => {
    const journeyAgg = journey.aggregator_sales.find(
      (aggSale: any) => aggSale.aggregator.id === +watch("aggregator_id")
    );
    const aggFeeWithoutTax =
      journeyAgg?.aggregator_fee - journeyAgg?.aggregator_fee_tax;

    const aggFeePercentage = journeyAgg.aggregator_fee_percentage / 100;

    const expectCommissionForJourney =
      aggFeeWithoutTax + aggFeePercentage * -toNum(watch("compensation"));

    totalCommissionExpected += expectCommissionForJourney;
  });

  const commissionExpected =
    mode === "add"
      ? totalCommissionExpected ?? 0
      : toNum(data?.data?.expected_aggregator_commission);

  const commissionActual = +watch("agg_fee") || 0;


  const deliveryExpected = aggCalcs.deliverFees ?? 0;
  const deliveryActual = +watch("delivery_cost") || 0;
 

  const totalExpected = commissionExpected + deliveryExpected;


  const posTotalSales =
    mode === "add" ? aggCalcs.salesWithTax : data?.data?.pos_total_sales;

  const finalDueBalance = posTotalSales - Number(watch("salesAsAggInv") ?? 0);

  const diffAllFee =
    commissionActual +
    deliveryActual +
    toNum(watch("bank_fee")) +
    toNum(watch("other_fee"));

  const differenceOfSales =
    mode === "add"
      ? posTotalSales -
        toNum(watch("salesAsAggInv")) -
        diffAllFee -
        toNum(watch("compensation")) -
        diffAllFee * ((selectedBranch?.vat ?? 0) / 100) -
        toNum(watch("restaurantDiscount") ?? 0) -
        toNum(watch("other_fee")) +
        toNum(watch("otherRevenue"))
      : data?.data?.total_sales_difference;

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (mode === "add") {
      const {
        branch_id,
        aggregator_id,
        agg_fee,
        delivery_cost,
        payment,
        journeys,
        bank_fee,
        other_fee,
        compensation,
        restaurantDiscount,
        otherRevenue,
        confirmedCompensation,
        prevMonthSales,
        nextMonthSales,
        note,
        compensationTax,
        other_cost
      } = data;
      const dataToSend: any = {
        branch_id: Number(branch_id),
        aggregator_id: Number(aggregator_id),
        aggregator_fee: agg_fee,
        // aggregator_fee_tax: aggCalcs.feeTax,
        delivery_fee: delivery_cost,
        // delivery_fee_tax: aggCalcs.deliveryFeeTax,
        // payment_methods: [+payment],
        net_due_invoice_balance: toNum(watch("salesAsAggInv")),
        pos_total_sales: posTotalSales,
        total_sales_difference: differenceOfSales,
        bank_fee,
        other_fee,
        compensation,
        compensation_tax: compensationTax,
        other_cost,
        restaurant_discount: restaurantDiscount,
        other_revenue_total: otherRevenue,
        confirmed_compensation: confirmedCompensation,
        previous_month_sales: prevMonthSales,
        next_month_sales: nextMonthSales,
        expected_aggregator_commission: commissionExpected,
        expected_delivery_cost: deliveryExpected,
        expected_total_cost: totalExpected,
        final_due_balance: finalDueBalance,
        ...(note && { note: note }),
      };
      const formData = new FormData();
      // append data
      for (const key in dataToSend) {
        formData.append(key, dataToSend[key]);
      }
      // append files
      if (files.length > 0) {
        files.forEach((file: any, index: number) => {
          formData.append(`files[${index}]`, file);
        });
      }
      formData.append(`payment_methods[0]`, payment);
      // append payments array
      journeys.forEach((journey, index: number) => {
        formData.append(`journeys[${index}]`, `${journey}`);
      });
      mutate(formData);
    }
  };

  const navigateToAggregators = () => {
    navigate("/aggregators/fees");
  };

  useEffect(() => {
    if (journeysData && mode === "add") {
      const journeysList: any[] = [];
      watch("journeys").forEach((journeyId) => {
        const selectedJourney = journeysData?.data.journeys.find(
          (journey: any) => journey.id === journeyId
        );
        journeysList.push(selectedJourney);
      });
      setSelectedJourneys(journeysList);
    }
  }, [watch("journeys")]);

  useEffect(() => {
    if (mode === "add") {
      let aggFee = 0;
      let deliveryFee = 0;
      let totalFeeTax = 0;
      let totalDeliveryFeeTax = 0;
      let totalSalesWithTax = 0;
      selectedJourneys.forEach((journey) => {
        journey.aggregator_sales.map((aggSale: any) => {
          if (aggSale.aggregator.id === +watch("aggregator_id")) {
            aggFee += aggSale.aggregator_fee - aggSale.aggregator_fee_tax;
            deliveryFee += aggSale.delivery_fee - aggSale.delivery_fee_tax;
            totalFeeTax += aggSale.aggregator_fee_tax;
            totalDeliveryFeeTax += aggSale.delivery_fee_tax;
            totalSalesWithTax += aggSale.sales;
          }
        });
      });
      setAggCalcs({
        basicAggFees: aggFee,
        deliverFees: deliveryFee,
        feeTax: totalFeeTax,
        deliveryFeeTax: totalDeliveryFeeTax,
        salesWithTax: totalSalesWithTax,
      });
    }
  }, [selectedJourneys]);

  //Get agg fee by Id success and error handling
  useEffect(() => {
    if (getAggFeeByIdStatus === "success") {
      reset({
        branch_id: data.data.branch.id,
        journeys: data.data.journeys,
        aggregator_id: data.data.aggregator.id,
        salesAsAggInv: data.data.net_due_invoice_balance,
        agg_fee: data.data.aggregator_fee ?? 0,
        delivery_cost: data.data.delivery_fee ?? 0,
        bank_fee: data.data.bank_fee ?? 0,
        other_fee: data.data.other_fee ?? 0,
        other_cost: data.data.other_cost ?? 0,
        compensation: data.data.compensation ?? 0,
        compensationTax: data.data.compensation_tax ?? 0,
        payment: data.data.payments[0]?.payment_method?.id,
        restaurantDiscount: data.data.restaurant_discount ?? 0,
        otherRevenue: data.data.other_revenue_total ?? 0,
        confirmedCompensation: data.data.confirmed_compensation ?? 0,
        prevMonthSales: data.data.previous_month_sales ?? 0,
        nextMonthSales: data.data.next_month_sales ?? 0,
        note: data.data.note ?? "",
      });
      setAggCalcs({
        basicAggFees: 5, //Random num
        deliverFees: data.data.expected_delivery_cost ?? 0, //Random num
        feeTax: 5, //Random num
        deliveryFeeTax: 5, //Random num
        salesWithTax: 5, //Random num
      });
    }
  }, [getAggFeeByIdStatus, journeysData, aggregatorsData]);

  useEffect(() => {
    setValue("aggregator_id", "");
  }, [watch("branch_id")]);

  useEffect(() => {
    setValue("journeys", []);
    setValue("payment", "");
  }, [watch("aggregator_id")]);

  //Add agg cost/fee success and error handling
  useEffect(() => {
    if (status === "success") {
      navigateToAggregators();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update agg cost/fee success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: errorUpdate?.data.errors?.[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  console.log({ selectedJourneys, aggCalcs, errors });

  const selectedJourneysToViewInViewMode = data?.data.journey_datas
    .map((journey: any) => {
      return `${journey?.start_date || ""} ${generalT("to")} ${
        journey?.end_date || ""
      }`;
    })
    .join(", ");

  const tutorials = [
    {
      label: "keta",
      link: "ffffff",
      icon: Keta,
    },
    {
      label: "hungerStation",
      link: "ffffff",
      icon: HungerStation,
    },
    {
      label: "jahez",
      link: "ffffff",
      icon: Jahez,
    },
    {
      label: "mrsool",
      link: "ffffff",
      icon: Mrsool,
    },
    {
      label: "noon",
      link: "ffffff",
      icon: Noon,
    },
  ];

  return (
    <Stack height={"100%"}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ height: "100%" }}>
        <Stack height={"100%"} justifyContent={"space-between"}>
          <Box p={{ xs: 2, sm: 4 }}>
            <Stack direction={"row"} spacing={"12px"} mb={3}>
              <Box
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                width={"60px"}
                height={"60px"}
                bgcolor={"#D0D5DD"}
                borderRadius={"16px"}
              >
                <img src={handshakeIcon} width={"32px"} alt="" />
              </Box>
              <Box>
                <Typography fontSize={"32px"} lineHeight={"38.4px"}>
                  {t("aggs")}
                </Typography>
                {mode === "edit" && (
                  <Typography
                    fontSize={"20px"}
                    lineHeight={"24px"}
                    color={"#98A2B3"}
                  >
                    {t("lastUpdated")}: {data?.data.updated_at}
                  </Typography>
                )}
              </Box>
            </Stack>
            {/* divider */}
            <Box bgcolor={"#F2F4F7"} width={"100%"} />
            <Stack spacing={1} direction={"row"} mb={3} alignItems={"center"}>
              <Typography
                color={"#475467"}
                lineHeight={"19.2px"}
                onClick={navigateToAggregators}
                sx={{ cursor: "pointer" }}
              >
                {t("aggs")}
              </Typography>
              <img src={RightArrow} alt="" />
              <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
                <Typography
                  color={"#344054"}
                  fontWeight={600}
                  lineHeight={"19.2px"}
                >
                  {mode === "add" ? t("addNew") : t("edit")} {t("aggsFeesCost")}
                </Typography>
              </Box>
            </Stack>
            {(!data || !aggregatorsData || !selectedAgg) && mode === "edit" ? (
              <Box
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                minHeight={"350px"}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={1.5} alignItems={"center"} width={"100%"}>
                <Stack
                  sx={{
                    minWidth: { xs: "100%", md: 872 },
                    maxWidth: { xs: "100%", md: 872 },
                  }}
                  bgcolor={"#EAECF0"}
                  px={3}
                  py={2}
                  alignItems={"center"}
                  borderRadius={"4px"}
                >
                  <Typography fontWeight={600} fontSize={"18px"} mb={2}>
                    {t("tutorialTitle")}
                  </Typography>
                  <Stack direction={"row"} gap={3}>
                    {tutorials.map((item) => {
                      return (
                        <Stack key={item.label} alignItems={"center"}>
                          <Box borderRadius={"6px"} overflow={"hidden"}>
                            <img
                              width={60}
                              height={60}
                              src={item.icon}
                              alt=""
                            />
                          </Box>
                          <Typography fontSize={"12px"}>
                            {t(`${item.label}`)}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>
                {/*  Branch Select */}
                <Stack
                  sx={{
                    minWidth: { xs: "100%", md: 872 },
                    maxWidth: { xs: "100%", md: 872 },
                  }}
                  gap={1.5}
                >
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.branch_id}
                    disabled={mode === "edit"}
                  >
                    <InputLabel id="demo-simple-select-label">
                      {t("branchLocation")}
                    </InputLabel>
                    <Controller
                      name="branch_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          key={field.value}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label={t("branchLocation")}
                        >
                          {branchesHavePermission?.map(({ id, name }) => {
                            return (
                              <MenuItem key={id} value={`${id}`}>
                                {name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    />
                    <FormHelperText id="my-helper-text">
                      {errors.branch_id?.message}
                    </FormHelperText>
                  </FormControl>
                  {/* Aggregators Select  */}
                  <Box
                    sx={
                      {
                        // display: mode === "add" ? "block" : "none",
                      }
                    }
                  >
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!errors.aggregator_id}
                      disabled={mode === "edit"}
                    >
                      <InputLabel id="demo-simple-select-label">
                        {t("agg")}
                      </InputLabel>
                      <Controller
                        name="aggregator_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            key={field.value}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label={t("agg")}
                          >
                            {aggregatorsData?.data?.map(({ id, name }: any) => {
                              return (
                                <MenuItem key={id} value={`${id}`}>
                                  {name}
                                </MenuItem>
                              );
                            })}
                            {aggregatorsData?.data.length === 0 &&
                              watch("journeys").length > 0 && (
                                <Typography
                                  align="center"
                                  color={"textSecondary"}
                                >
                                  {generalT("noData")}
                                </Typography>
                              )}
                          </Select>
                        )}
                      />
                      <FormHelperText id="my-helper-text">
                        {errors.aggregator_id?.message}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                  {/* Journey Select  */}
                  {mode === "add" ? (
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!errors.journeys}
                    >
                      <InputLabel id="demo-multiple-checkbox-label">
                        {t("selectJourneys")}
                      </InputLabel>
                      <Controller
                        name={`journeys`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="demo-multiple-checkbox-label"
                            id="demo-multiple-checkbox"
                            multiple
                            input={
                              <OutlinedInput label={t("selectJourneys")} />
                            }
                            renderValue={(selected) =>
                              selected
                                .map((selectedId) => {
                                  const journey =
                                    journeysData?.data.journeys?.find(
                                      (journey: any) =>
                                        journey.id === +selectedId
                                    );
                                  return `${
                                    journey?.start_date || ""
                                  } ${generalT("to")} ${
                                    journey?.end_date || ""
                                  }`;
                                })
                                .join(", ")
                            }
                          >
                            {journeysData?.data.journeys.map((journey: any) => (
                              <MenuItem key={journey.id} value={journey.id}>
                                <Checkbox
                                  checked={
                                    field.value &&
                                    field.value.indexOf(journey.id) > -1
                                  }
                                />
                                {journey?.start_date} {generalT("to")}{" "}
                                {journey?.end_date}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      <FormHelperText id="my-helper-text">
                        {errors.journeys?.message}
                      </FormHelperText>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      disabled
                      value={selectedJourneysToViewInViewMode || ""}
                      label={t("selectJourneys")}
                      multiline
                      size="small"
                    />
                  )}
                  {/* total sales */}
                  <Box
                    borderRadius={"4px"}
                    display={"flex"}
                    justifyContent={"center"}
                  >
                    <Typography
                      color={"#344054"}
                      fontWeight={600}
                      lineHeight={"19.2px"}
                      bgcolor={"#EAECF0"}
                      p={"4px 8px"}
                    >
                      {t("totalSales")}
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      {/* sales as agg invoice */}
                      <Controller
                        name="salesAsAggInv"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={t("netDueInvoiceBalance")}
                            {...field}
                            error={!!errors.salesAsAggInv}
                            helperText={errors.salesAsAggInv?.message}
                            fullWidth
                            size="small"
                            InputLabelProps={{
                              shrink: field.value !== undefined,
                            }}
                            disabled={mode === "edit"}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {/* total pos sales*/}
                      <TextField
                        type="number"
                        label={t("posTotalSales")}
                        value={posTotalSales}
                        fullWidth
                        size="small"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {/* difference */}
                      <TextField
                        type="number"
                        label={t("difference")}
                        value={differenceOfSales.toFixed(2)}
                        fullWidth
                        size="small"
                        disabled
                      />
                      {(differenceOfSales < -0.5 ||
                        differenceOfSales > 0.5) && (
                        <Typography fontSize={"13px"} color={"error"}>
                          {t("differenceExplanation")}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  {/* cost */}
                  <Box
                    borderRadius={"4px"}
                    display={"flex"}
                    justifyContent={"center"}
                  >
                    <Typography
                      color={"#344054"}
                      fontWeight={600}
                      lineHeight={"19.2px"}
                      bgcolor={"#EAECF0"}
                      p={"4px 8px"}
                    >
                      {t("cost")}
                    </Typography>
                  </Box>
                  {/* Agg Fee */}
                  <Controller
                    name="agg_fee"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        label={`${t("basicAggregatorCommission")} (${generalT(
                          "includeTax"
                        )})`}
                        {...field}
                        error={!!errors.agg_fee}
                        helperText={errors.agg_fee?.message}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: field.value !== undefined,
                        }}
                        disabled={mode === "edit"}
                      />
                    )}
                  />
                  {/* Delivery Fee */}
                  <Controller
                    name="delivery_cost"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        label={`${t("deliveryCost")} (${generalT(
                          "includeTax"
                        )})`}
                        {...field}
                        error={!!errors.delivery_cost}
                        helperText={errors.delivery_cost?.message}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: field.value !== undefined,
                        }}
                        disabled={mode === "edit"}
                      />
                    )}
                  />
                  {/* Bank Fee */}
                  <Controller
                    name="bank_fee"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        label={t("bankFeeEx")}
                        {...field}
                        error={!!errors.bank_fee}
                        helperText={errors.bank_fee?.message}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: field.value !== undefined,
                        }}
                        disabled={mode === "edit"}
                      />
                    )}
                  />
                  {/* Other Fee */}
                  <Controller
                    name="other_fee"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        label={t("otherFeesEx")}
                        {...field}
                        error={!!errors.other_fee}
                        helperText={errors.other_fee?.message}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: field.value !== undefined,
                        }}
                        disabled={mode === "edit"}
                      />
                    )}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      {/* Compensation */}
                      <Controller
                        name="compensation"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={t("compensation")}
                            {...field}
                            error={!!errors.compensation}
                            helperText={errors.compensation?.message}
                            fullWidth
                            size="small"
                            InputLabelProps={{
                              shrink: field.value !== undefined,
                            }}
                            disabled={mode === "edit"}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {/* Compensation  tax*/}
                      <Controller
                        name="compensationTax"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={t("compensationTax")}
                            {...field}
                            error={!!errors.compensationTax}
                            helperText={errors.compensationTax?.message}
                            fullWidth
                            size="small"
                            InputLabelProps={{
                              shrink: field.value !== undefined,
                            }}
                            disabled={mode === "edit"}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Other cost */}
                  <Box
                    borderRadius={"4px"}
                    display={"flex"}
                    justifyContent={"center"}
                  >
                    <Typography
                      color={"#344054"}
                      fontWeight={600}
                      lineHeight={"19.2px"}
                      bgcolor={"#EAECF0"}
                      p={"4px 8px"}
                    >
                      {t("otherCost")}
                    </Typography>
                  </Box>
                  <Grid item xs={12} md={6}>
                    {/* Restaurant Discount */}
                    <Controller
                      name="restaurantDiscount"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          type="number"
                          label={t("restaurantDiscount")}
                          {...field}
                          error={!!errors.restaurantDiscount}
                          helperText={errors.restaurantDiscount?.message}
                          fullWidth
                          size="small"
                          InputLabelProps={{
                            shrink: field.value !== undefined,
                          }}
                          disabled={mode === "edit"}
                        />
                      )}
                    />
                    <Typography fontSize={"13px"}>
                      {t("discountIntegrationNote")}
                    </Typography>
                  </Grid>
                  {/* Other cost */}
                  <Controller
                    name="other_cost"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        label={t("otherCost")}
                        {...field}
                        error={!!errors.other_cost}
                        helperText={errors.other_cost?.message}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: field.value !== undefined,
                        }}
                        disabled={mode === "edit"}
                      />
                    )}
                  />
                  {/* other revenue for restaurant */}
                  <Box
                    borderRadius={"4px"}
                    display={"flex"}
                    justifyContent={"center"}
                  >
                    <Typography
                      color={"#344054"}
                      fontWeight={600}
                      lineHeight={"19.2px"}
                      bgcolor={"#EAECF0"}
                      p={"4px 8px"}
                    >
                      {t("otherRevenueForRestaurant")}
                    </Typography>
                  </Box>
                  {/* other revenue total */}
                  <Controller
                    name="otherRevenue"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        label={t("otherRevenueTotal")}
                        {...field}
                        error={!!errors.otherRevenue}
                        helperText={errors.otherRevenue?.message}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: field.value !== undefined,
                        }}
                        disabled={mode === "edit"}
                      />
                    )}
                  />
                  {/* confirmed compensation */}
                  <Controller
                    name="confirmedCompensation"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        label={t("confirmedCompensation")}
                        {...field}
                        error={!!errors.confirmedCompensation}
                        helperText={errors.confirmedCompensation?.message}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: field.value !== undefined,
                        }}
                        disabled={mode === "edit"}
                      />
                    )}
                  />
                  {/* previous/after sales after 12am */}
                  <Box
                    borderRadius={"4px"}
                    display={"flex"}
                    justifyContent={"center"}
                  >
                    <Typography
                      color={"#344054"}
                      fontWeight={600}
                      lineHeight={"19.2px"}
                      bgcolor={"#EAECF0"}
                      p={"4px 8px"}
                    >
                      {t("previousAfterSales_12am")}
                    </Typography>
                  </Box>
                  {/* prev month sales */}
                  <Controller
                    name="prevMonthSales"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        label={t("previousMonthSales")}
                        {...field}
                        error={!!errors.prevMonthSales}
                        helperText={errors.prevMonthSales?.message}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: field.value !== undefined,
                        }}
                        disabled
                      />
                    )}
                  />
                  {/* next month sales */}
                  <Controller
                    name="nextMonthSales"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        label={t("nextMonthSales")}
                        {...field}
                        error={!!errors.nextMonthSales}
                        helperText={errors.nextMonthSales?.message}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: field.value !== undefined,
                        }}
                        disabled
                      />
                    )}
                  />

                  {/* summary */}
                  <Box
                    borderRadius={"4px"}
                    display={"flex"}
                    justifyContent={"center"}
                  >
                    <Typography
                      color={"#344054"}
                      fontWeight={600}
                      lineHeight={"19.2px"}
                      bgcolor={"#EAECF0"}
                      p={"4px 8px"}
                    >
                      {t("summary")}
                    </Typography>
                  </Box>

                  {/* Agg payment */}
                  {mode === "add" ? (
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!errors.payment}
                    >
                      <InputLabel id="demo-simple-select-label">
                        {t("selectAggregatorPayment")}
                      </InputLabel>
                      <Controller
                        name="payment"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            key={field.value}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label={t("selectAggregatorPayment")}
                          >
                            {selectedAgg?.related_payment_methods
                              .filter(
                                (payment: any) =>
                                  !payment.payment_method.is_cash
                              )
                              .map(({ payment_method }: any) => {
                                return (
                                  <MenuItem
                                    key={payment_method.id}
                                    value={`${payment_method.id}`}
                                  >
                                    {payment_method.name}
                                  </MenuItem>
                                );
                              })}
                          </Select>
                        )}
                      />
                      <FormHelperText id="my-helper-text">
                        {errors.payment?.message}
                      </FormHelperText>
                    </FormControl>
                  ) : (
                    <FormControl
                      fullWidth
                      size="small"
                      // disabled={mode === "edit"}
                    >
                      <InputLabel id="demo-simple-select-label">
                        {t("selectAggregatorPayment")}
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label={t("selectAggregatorPayment")}
                        value={data?.data.payments[0]?.payment_method?.id}
                      >
                        {selectedAgg?.related_payment_methods
                          .filter(
                            (payment: any) => !payment.payment_method.is_cash
                          )
                          .map(({ payment_method }: any) => {
                            return (
                              <MenuItem
                                key={payment_method.id}
                                value={`${payment_method.id}`}
                              >
                                {payment_method.name}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                  )}
                  <TextField
                    size="small"
                    fullWidth
                    label={t("finalDueBalance")}
                    value={finalDueBalance.toFixed(2)}
                    disabled
                  />
                  {/* note */}
                  <Controller
                    name="note"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label={generalT("notesOptional")}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        maxRows={3}
                        {...field}
                        error={!!errors.note}
                        helperText={errors.note?.message}
                        disabled={mode === "edit"}
                      />
                    )}
                  />
                  {/* files */}
                  {mode === "add" ? (
                    <Box width={"100%"} mt={1}>
                      <FilePondUploader
                        onUpload={(e) => setFiles(e)}
                        maxFiles={3}
                        maxFileSize={3}
                        acceptedFileTypes={[
                          "image/*",
                          "application/pdf",
                          "text/csv",
                          "application/vnd.ms-excel",
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        ]}
                      />
                    </Box>
                  ) : (
                    <>
                      <Typography>{generalT("attachments")}</Typography>
                      <Stack
                        gap={3}
                        direction={"row"}
                        maxWidth={"50%"}
                        flexWrap={"wrap"}
                      >
                        {data?.data.files.map((attachment: any) => {
                          return (
                            <FileItem
                              isDeletable={false}
                              key={attachment.id}
                              name={`${attachment.file.name} .${attachment.file.extension}`}
                              info={formatBytes(attachment.file.size)}
                              isDownloadable
                              onDownload={() => {
                                downloadFileFromUrl(
                                  attachment.file.url,
                                  attachment.file.name
                                );
                              }}
                            />
                          );
                        })}
                      </Stack>
                    </>
                  )}
                  <Controller
                    name="sendEmail"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        disabled={true}
                        control={<Checkbox {...field} color="primary" />}
                        label={t("sendEmailAggregator")}
                        checked={field.value}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            )}
          </Box>

          {mode === "add" && (
            <Box
              p={{ xs: " 24px 16px", sm: 4 }}
              bgcolor={"#FCFCFD"}
              display={"flex"}
              justifyContent={"center"}
              boxShadow={"0 -8px 50px -5px rgba(50, 50, 50, 0.1)"}
            >
              <Stack
                direction={"row"}
                sx={{ minWidth: { xs: "100%", md: 872 } }}
                justifyContent={"space-between"}
              >
                <Button
                  variant="outlined"
                  color="tertiary"
                  onClick={navigateToAggregators}
                >
                  {t("cancel")}
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  endIcon={<KeyboardArrowRightIcon />}
                  loading={isPending || isPendingUpdate}
                >
                  {t("submit")}
                </LoadingButton>
              </Stack>
            </Box>
          )}
        </Stack>
      </form>
    </Stack>
  );
};

export default AddEditAggregatorFees;
